import { Inject, Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import type { FirebaseApp } from 'firebase/app';
import type { Messaging } from 'firebase/messaging';
import { environment } from '../../../../environments/environment';
import { API_BASE } from './ordering-api';

/** Service worker that renders background pushes. Lives at the site root (public/ → /). */
const SW_URL = '/firebase-messaging-sw.js';
/** A dedicated scope so this SW never collides with any future app-level service worker. */
const SW_SCOPE = '/firebase-cloud-messaging-push-scope';
/** Last token we successfully registered — lets us skip redundant PUTs on every app open. */
const TOKEN_CACHE_KEY = 'orderyaa.push.token';

/**
 * Browser Web Push (Firebase Cloud Messaging) for the customer. Registers the device token with the
 * API ({@code PUT /api/public/device/push-token}) so order-lifecycle notifications can reach this
 * browser, and clears it on sign-out. The customer JWT is attached by customerAuthInterceptor.
 *
 * Everything is best-effort and SSR-safe: no-ops on the server, when the browser lacks push support,
 * or when Firebase config is absent (empty apiKey/appId/vapidKey ⇒ feature simply off). The heavy
 * Firebase SDK is loaded lazily via dynamic import so it never enters the server bundle or the
 * initial browser chunk.
 */
@Injectable({ providedIn: 'root' })
export class PushService {
  private readonly http = inject(HttpClient);
  private readonly isBrowser: boolean;

  private appPromise: Promise<FirebaseApp | null> | null = null;
  private foregroundBound = false;
  private syncing = false;

  constructor(@Inject(PLATFORM_ID) platformId: Object) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  /** True only when a full Web config + VAPID key have been supplied. */
  private get configured(): boolean {
    const c = environment.firebase;
    return !!(c && c.apiKey && c.projectId && c.appId && c.messagingSenderId && environment.firebaseVapidKey);
  }

  /** This browser can actually receive Web Push (secure context, SW + Notification + Push APIs). */
  private get supported(): boolean {
    return this.isBrowser
      && typeof Notification !== 'undefined'
      && typeof PushManager !== 'undefined'
      && 'serviceWorker' in navigator;
  }

  /**
   * Call after a FRESH sign-in. Requests notification permission if the user hasn't decided yet,
   * then registers this device's token. Safe to await or fire-and-forget.
   */
  async register(): Promise<void> {
    if (!this.supported || !this.configured) return;
    try {
      let permission = Notification.permission;
      if (permission === 'default') permission = await Notification.requestPermission();
      if (permission !== 'granted') return;
      // Fresh sign-in: drop any cached token so syncToken always re-registers — a device that switched
      // customers (no logout in between) must re-point its server-side token to the new account.
      this.clearLocal();
      await this.syncToken();
    } catch {
      /* best-effort — never break the sign-in flow over push */
    }
  }

  /**
   * Call on app startup for an already-signed-in customer. Only refreshes the token when permission
   * was ALREADY granted, so a returning user is never re-prompted; the endpoint expects a fresh
   * token on each app open (FCM rotates tokens).
   */
  async syncIfPermitted(): Promise<void> {
    if (!this.supported || !this.configured) return;
    if (Notification.permission !== 'granted') return;
    try {
      await this.syncToken();
    } catch {
      /* best-effort */
    }
  }

  /**
   * Call on sign-out. Clears the token server-side (must run while the session token is still
   * attached) and deletes it from FCM so this browser stops receiving pushes.
   */
  async unregister(): Promise<void> {
    if (!this.isBrowser) return;
    // Fire the server clear FIRST (interceptor still has the live token at this point).
    try {
      await firstValueFrom(this.http.delete(`${API_BASE}/api/public/device/push-token`));
    } catch {
      /* ignore */
    }
    this.clearLocal();
    if (!this.supported || !this.configured) return;
    try {
      const app = await this.ensureApp();
      if (!app) return;
      const { getMessaging, deleteToken, isSupported } = await import('firebase/messaging');
      if (!(await isSupported())) return;
      await deleteToken(getMessaging(app));
    } catch {
      /* ignore */
    }
  }

  /** Forget the cached token locally (e.g. when the session is torn down without a server round-trip). */
  clearLocal(): void {
    if (this.isBrowser) localStorage.removeItem(TOKEN_CACHE_KEY);
  }

  // ── internals ──────────────────────────────────────────────────────────────

  private async syncToken(): Promise<void> {
    if (this.syncing) return;
    this.syncing = true;
    try {
      const { getMessaging, getToken, isSupported } = await import('firebase/messaging');
      if (!(await isSupported())) return;

      const app = await this.ensureApp();
      if (!app) return;

      const registration = await navigator.serviceWorker.register(SW_URL, { scope: SW_SCOPE });
      const messaging = getMessaging(app);
      const token = await getToken(messaging, {
        vapidKey: environment.firebaseVapidKey,
        serviceWorkerRegistration: registration,
      });
      if (!token) return;

      this.bindForeground(messaging);

      if (localStorage.getItem(TOKEN_CACHE_KEY) === token) return; // unchanged — nothing to send
      await firstValueFrom(
        this.http.put(`${API_BASE}/api/public/device/push-token`, { token, platform: 'web' }),
      );
      localStorage.setItem(TOKEN_CACHE_KEY, token);
    } finally {
      this.syncing = false;
    }
  }

  private ensureApp(): Promise<FirebaseApp | null> {
    this.appPromise ??= (async () => {
      const { initializeApp, getApps, getApp } = await import('firebase/app');
      return getApps().length ? getApp() : initializeApp(environment.firebase);
    })().catch(() => null);
    return this.appPromise;
  }

  /** Foreground messages don't auto-display — surface them as a native notification once. */
  private async bindForeground(messaging: Messaging): Promise<void> {
    if (this.foregroundBound) return;
    this.foregroundBound = true;
    const { onMessage } = await import('firebase/messaging');
    onMessage(messaging, (payload) => {
      const n = payload.notification;
      if (!n?.title || Notification.permission !== 'granted') return;
      try {
        new Notification(n.title, { body: n.body ?? '', icon: '/images/logo.png' });
      } catch {
        // Some (mostly mobile) browsers forbid the Notification constructor — ignore; the
        // background SW path still delivers when the tab isn't focused.
      }
    });
  }
}
